/**
 * BidRequirementsPanel — the "how to bid" workflow data: when it's due, how to
 * submit, who the contact is, whether samples are required, mandatory documents
 * and evaluation criteria. Uses the tender-analysis CSS vocabulary
 * (summary-section / products-table / decision-card / timeline-grid) so it
 * matches the analysis tabs visually.
 */
import React, { useState } from 'react';
import { RefreshCw, AlertCircle, ClipboardList } from 'lucide-react';
import { forgeAPI } from '../../../services/api';
import { useForge } from '../ForgeContext';

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return d; }
};

const Card = ({ label, value, tone = 'green' }) => (
  <div className={`decision-card ${tone}`}>
    <div className="decision-header"><span className="decision-type">{label}</span></div>
    <p className="decision-reason" style={{ fontWeight: 600 }}>{value || '—'}</p>
  </div>
);

const BidRequirementsPanel = () => {
  const { tenderId, summary } = useForge();
  const [data, setData] = useState(summary?.bid_requirements || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // No auto-build on mount: building calls bid_agents Phase 4 (slow). Show
  // cached requirements instantly; extract on demand.
  const load = async (refresh = false) => {
    setLoading(true); setError(null);
    try {
      setData(await forgeAPI.getBidRequirements(tenderId, refresh));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  if (error) return <div className="forge-error">{error}</div>;

  if (!data) {
    return (
      <div className="forge-empty">
        <ClipboardList size={28} />
        <h3>Bid requirements not extracted yet</h3>
        <p className="forge-muted">Pull the due date, submission method, contact, samples and
          mandatory documents from the tender + analysis.</p>
        <button className="forge-btn-primary" onClick={() => load(false)} disabled={loading}>
          {loading ? 'Extracting…' : 'Extract bid requirements'}
        </button>
      </div>
    );
  }

  const poc = data.point_of_contact || {};
  const dueSoon = data.closing_date && new Date(data.closing_date) > new Date();

  return (
    <div className="forge-bidreq">
      <div className="forge-spec-toolbar">
        <div className="forge-muted">
          How to bid · source: {data.source || 'tender'} · confidence {data.confidence}
        </div>
        <button className="forge-btn-ghost" onClick={() => load(true)} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'forge-spin' : ''} /> Re-extract
        </button>
      </div>

      {/* Submission essentials */}
      <div className="summary-section">
        <h4 className="summary-section-title">Submission</h4>
        <div className="decision-grid">
          <div className="decision-card amber">
            <div className="decision-header"><span className="decision-type">Closing date</span></div>
            <p className="decision-reason" style={{ fontWeight: 700 }}>{fmtDate(data.closing_date)}</p>
            {data.time_zone && <span className="forge-muted">{data.time_zone}</span>}
            {dueSoon && <span className="days-badge ok" style={{ marginTop: 6 }}>Open</span>}
          </div>
          <Card label="How to submit" value={data.submission_method} />
          {data.submission_format && <Card label="Format" value={data.submission_format} />}
          {data.page_limit && <Card label="Page limit" value={`${data.page_limit} pages`} />}
          {data.language && <Card label="Language" value={data.language} />}
          {data.submission_address && <Card label="Where" value={data.submission_address} />}
        </div>
      </div>

      {/* Point of contact + samples */}
      <div className="summary-section">
        <h4 className="summary-section-title">Point of Contact & Samples</h4>
        <div className="decision-grid">
          <div className="decision-card green">
            <div className="decision-header"><span className="decision-type">Contracting contact</span></div>
            <p className="decision-reason" style={{ fontWeight: 600 }}>{poc.name || '—'}</p>
            {poc.title && <span className="forge-muted">{poc.title}</span>}
            {poc.email && <div className="forge-muted">{poc.email}</div>}
            {poc.phone && <div className="forge-muted">{poc.phone}</div>}
          </div>
          <div className={`decision-card ${data.samples_required ? 'amber' : 'green'}`}>
            <div className="decision-header"><span className="decision-type">Samples required</span></div>
            <p className="decision-reason" style={{ fontWeight: 700 }}>
              {data.samples_required ? 'Yes' : 'No'}
            </p>
            {data.sample_notes && <span className="forge-muted">{data.sample_notes}</span>}
          </div>
        </div>
      </div>

      {/* Mandatory documents */}
      {data.mandatory_documents?.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title">Mandatory Documents</h4>
          <table className="products-table">
            <thead><tr><th>Document</th><th>Form ID</th><th>Section</th><th>Notes</th></tr></thead>
            <tbody>
              {data.mandatory_documents.map((d, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{d.document}</td>
                  <td>{d.form_id ? <span className="heuristic-chip">{d.form_id}</span> : '—'}</td>
                  <td>{d.section_ref ? <span className="heuristic-chip">{d.section_ref}</span> : '—'}</td>
                  <td style={{ fontSize: 12, color: '#6b7280' }}>{d.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Evaluation criteria */}
      {data.evaluation_criteria?.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title">Evaluation Criteria</h4>
          <table className="products-table">
            <thead><tr><th>Criterion</th><th>Weight</th><th style={{ width: '28%' }}>Visual</th><th>Section</th><th>Details</th></tr></thead>
            <tbody>
              {data.evaluation_criteria.map((c, i) => {
                const w = parseFloat(c.weight_pct) || 0;
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{c.criterion}</td>
                    <td style={{ fontWeight: 700 }}>{w > 0 ? `${w}%` : 'P/F'}</td>
                    <td>
                      <div className="eval-bar-track">
                        <div className="eval-bar-fill" style={{ width: `${w}%`, background: 'linear-gradient(90deg, #2563eb, #6366f1)' }} />
                      </div>
                    </td>
                    <td>{c.section_ref ? <span className="heuristic-chip">{c.section_ref}</span> : '—'}</td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>{c.details}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Key dates */}
      {data.key_dates?.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title">Key Dates</h4>
          <div className="timeline-grid">
            {data.key_dates.map((kd, i) => {
              const past = kd.date && new Date(kd.date) < new Date();
              return (
                <div key={i} className={`timeline-item ${past ? '' : 'urgent'}`}>
                  <span className="timeline-label">{kd.event}</span>
                  <span className="timeline-value">{fmtDate(kd.date)}</span>
                  <span className={`days-badge ${past ? 'warning' : 'ok'}`}>{past ? 'Past' : 'Upcoming'}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.confidence === 'low' && (
        <div className="forge-draft-banner">
          <AlertCircle size={13} /> Low-confidence extraction — verify against the solicitation document.
        </div>
      )}
    </div>
  );
};

export default BidRequirementsPanel;
